import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Grid, 
  Paper, 
  useTheme as useMuiTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { motion } from 'framer-motion';
import ComputerIcon from '@mui/icons-material/Computer';
import CodeIcon from '@mui/icons-material/Code';
import AppsIcon from '@mui/icons-material/Apps';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import SecurityIcon from '@mui/icons-material/Security';
import WebIcon from '@mui/icons-material/Web';
import TokenIcon from '@mui/icons-material/Token';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ShieldIcon from '@mui/icons-material/Shield';

const ServiceCard = ({ icon, title, delay, onClick }) => {
  const { isDarkMode } = useTheme();
  const muiTheme = useMuiTheme();
  
  return (
    <Grid item xs={12} sm={6} md={4}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay, type: "spring", stiffness: 300, damping: 20 }}
        whileHover={{ 
          rotateY: 5, 
          rotateX: -5, 
          scale: 1.02
        }}
        style={{
          perspective: 1000,
          transformStyle: 'preserve-3d',
        }}
      >
        <Paper
          elevation={2}
          onClick={onClick}
          sx={{
            p: 3,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            borderRadius: 2,
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            backgroundColor: isDarkMode ? 'background.paper' : 'background.paper',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: muiTheme.shadows[8],
              backgroundColor: isDarkMode 
                ? 'rgba(46, 91, 255, 0.08)' 
                : 'rgba(46, 91, 255, 0.04)',
            },
          }}
        >
          <motion.div
            whileHover={{ scale: 1.2, rotate: 360 }}
            transition={{ duration: 0.5, type: "spring", stiffness: 300 }}
            style={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 16,
              background: 'linear-gradient(135deg, #2E5BFF 0%, #00C896 100%)',
              color: 'white',
            }}
          >
            {icon}
          </motion.div>
          <Typography variant="h6" component="h3" gutterBottom>
            {title}
          </Typography>
        </Paper>
      </motion.div>
    </Grid>
  );
};

const Services = () => {
  const { t, language, servicesList } = useLanguage();
  const [selectedService, setSelectedService] = useState(null);

  const iconMap = {
    computer: <ComputerIcon fontSize="large" />,
    code: <CodeIcon fontSize="large" />,
    apps: <AppsIcon fontSize="large" />,
    desktop: <DesktopWindowsIcon fontSize="large" />,
    security: <SecurityIcon fontSize="large" />,
    web: <WebIcon fontSize="large" />,
    token: <TokenIcon fontSize="large" />,
    psychology: <PsychologyIcon fontSize="large" />,
    shield: <ShieldIcon fontSize="large" />,
  };
  
  const defaultServices = [
    { icon: <ComputerIcon fontSize="large" />, title: t('service1'), desc: t('service1Desc'), delay: 0.1 },
    { icon: <CodeIcon fontSize="large" />, title: t('service2'), desc: t('service2Desc'), delay: 0.2 },
    { icon: <AppsIcon fontSize="large" />, title: t('service3'), desc: t('service3Desc'), delay: 0.3 },
    { icon: <DesktopWindowsIcon fontSize="large" />, title: t('service4'), desc: t('service4Desc'), delay: 0.4 },
    { icon: <SecurityIcon fontSize="large" />, title: t('service5'), desc: t('service5Desc'), delay: 0.5 },
    { icon: <WebIcon fontSize="large" />, title: t('service6'), desc: t('service6Desc'), delay: 0.6 },
    { icon: <TokenIcon fontSize="large" />, title: t('service7'), desc: t('service7Desc'), delay: 0.7 },
    { icon: <PsychologyIcon fontSize="large" />, title: t('service8'), desc: t('service8Desc'), delay: 0.8 },
    { icon: <ShieldIcon fontSize="large" />, title: t('service9'), desc: t('service9Desc'), delay: 0.9 },
  ];

  const services = Array.isArray(servicesList) && servicesList.length > 0
    ? servicesList
        .filter(s => s.enabled !== false)
        .map((s, idx) => ({
          icon: iconMap[s.icon] || <ComputerIcon fontSize="large" />,
          title: (s.title && s.title[language]) || s.title?.en || t(`service${idx + 1}`),
          desc: (s.desc && s.desc[language]) || s.desc?.en || t(`service${idx + 1}Desc`),
          delay: (idx + 1) * 0.1,
        }))
    : defaultServices;

  const handleClose = () => setSelectedService(null);

  return (
    <Box
      id="services"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: theme => theme.palette.background.default,
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Typography
            variant="h2"
            component="h2"
            align="center"
            gutterBottom
            sx={{
              mb: 6,
              fontWeight: 700,
              background: 'linear-gradient(45deg, #2E5BFF, #00C896)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {t('servicesTitle')}
          </Typography>
        </motion.div>

        <Grid container spacing={4}>
          {services.map((service, index) => (
            <ServiceCard
              key={index}
              icon={service.icon}
              title={service.title}
              delay={service.delay}
              onClick={() => setSelectedService(service)}
            />
          ))}
        </Grid>
      </Container>

      <Dialog
        open={Boolean(selectedService)}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
            background: theme => theme.palette.mode === 'dark' ? 'rgba(30, 30, 40, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid',
            borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
          }
        }}
      >
        <DialogTitle component="div" sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #2E5BFF 0%, #00C896 100%)',
              color: 'white',
            }}
          >
            {selectedService?.icon}
          </Box>
          <Typography variant="h5" fontWeight={700}>
            {selectedService?.title}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: 'rgba(0, 0, 0, 0.05)' }}>
          <Typography variant="body1" sx={{ py: 2, lineHeight: 1.8, color: 'text.secondary' }}>
            {selectedService?.desc}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleClose} 
            variant="contained" 
            sx={{ 
              borderRadius: 2,
              px: 4,
              py: 1,
              background: 'linear-gradient(135deg, #2E5BFF 0%, #00C896 100%)',
              '&:hover': {
                opacity: 0.9,
              }
            }}
          >
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Services;
